import { useTranslation } from 'react-i18next'

import { ShipmentStatus } from '#/shared/api/generated/shipment/models'
import type { ShipmentDetail } from '#/shared/api/generated/shipment/models'
import { cn } from '#/shared/lib/utils'
import { Button } from '#/shared/ui/button'
import { ArrowForwardIcon } from '#/shared/ui/icons'
import { PostToLBDropdown, PostedActions } from '#/widgets/detail-toolbar'

import { isDispatchedStatus } from '../lib/order-statuses'
import { SHIPMENT_STAGES } from '../model/stages'
import type { ShipmentStage } from '../model/stages'

interface ShipmentPrimaryActionProps {
  stage: ShipmentStage
  shipment?: ShipmentDetail
  compact?: boolean
  onConvert?: () => void
  onDispatch?: () => void
  onMarkStatus?: (status: ShipmentStatus) => void
  onClose?: () => void
  onPosted?: () => void
}

/** Physical-progress transitions an order can be advanced through from the
 *  toolbar: dispatched → picked up → completed. Shown in both the full detail
 *  page and the sidebar. */
const NEXT_STATUS_ACTION: Partial<
  Record<string, { status: ShipmentStatus; labelKey: string }>
> = {
  [ShipmentStatus.dispatched]: {
    status: ShipmentStatus.picked_up,
    labelKey: 'detailSidebar.markAsPickedup',
  },
  [ShipmentStatus.picked_up]: {
    status: ShipmentStatus.completed,
    labelKey: 'detailSidebar.markAsCompleted',
  },
}

function ConvertButton({
  labelKey,
  compact,
  outline,
  onConvert,
}: {
  labelKey: string
  compact?: boolean
  outline?: boolean
  onConvert?: () => void
}) {
  const { t } = useTranslation()
  const isOutline = outline && !compact
  return (
    <Button
      variant={isOutline ? 'outline' : undefined}
      size="xs"
      className={cn(
        'group',
        compact || isOutline ? 'gap-2.5 px-2.5' : 'gap-2.5 px-3.5',
      )}
      onClick={onConvert}
    >
      {t(labelKey)}
      <ArrowForwardIcon className="size-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
    </Button>
  )
}

export function ShipmentPrimaryAction({
  stage,
  shipment,
  compact,
  onConvert,
  onDispatch,
  onMarkStatus,
  onClose,
  onPosted,
}: ShipmentPrimaryActionProps) {
  const { t } = useTranslation()
  if (stage === SHIPMENT_STAGES.LEAD) {
    return (
      <ConvertButton
        labelKey="leadDetails.toolbar.convertToOrder"
        compact={compact}
        outline
        onConvert={onConvert}
      />
    )
  }
  if (stage === SHIPMENT_STAGES.QUOTE) {
    return (
      <ConvertButton
        labelKey="quoteDetails.toolbar.convertToOrder"
        compact={compact}
        onConvert={onConvert}
      />
    )
  }
  if (!shipment) return null
  const nextStatus = NEXT_STATUS_ACTION[shipment.status]
  if (nextStatus) {
    return (
      <Button
        variant="outline"
        size="xs"
        className="px-2.5 text-gray-900"
        onClick={() => onMarkStatus?.(nextStatus.status)}
      >
        {t(nextStatus.labelKey)}
      </Button>
    )
  }
  if (shipment.status === ShipmentStatus.posted) {
    return (
      <PostedActions
        shipment={shipment}
        compact={compact}
        onDispatch={onDispatch}
        onRemoved={onClose}
      />
    )
  }
  // A carrier is already assigned (dispatched/not-signed/picked-up/completed) —
  // posting to a load board no longer applies.
  if (isDispatchedStatus(shipment.status)) return null
  return (
    <PostToLBDropdown
      label={t('detailToolbar.postToLB')}
      shipmentId={shipment.id}
      onPosted={onPosted ?? onClose}
    />
  )
}
